/**
 * BookingCancellationGuide Component
 *
 * Guide-initiated cancellation flow. Simpler than customer flow
 * because guide cancellations always result in 100% refund.
 *
 * API Endpoint:
 *   POST /api/v1/guides/bookings/:bookingId/cancel
 *
 * Story: NEW-003-FE - Guide Cancellation Request Flow (UI)
 * MVP: 1.5.2 - Booking Payment Domain Model
 */

import React, { useState } from 'react';
import PropTypes from 'prop-types';
import apiRequest from '../../services/api';
import Modal from '../shared/Modal';
import {
  AlertTriangle,
  CheckCircle,
  XCircle,
  AlertCircle
} from 'lucide-react';

const GUIDE_REASONS = [
  { value: 'weather_unsafe', label: 'Unsafe weather conditions' },
  { value: 'safety_concern', label: 'Safety concern' },
  { value: 'emergency', label: 'Personal emergency' },
  { value: 'equipment_issue', label: 'Equipment problem' },
  { value: 'scheduling_conflict', label: 'Scheduling conflict' },
  { value: 'customer_noncompliance', label: 'Customer non-compliance' },
  { value: 'other', label: 'Other reason' },
];

export default function BookingCancellationGuide({ isOpen, onClose, bookingId, onCancelled }) {
  const [step, setStep] = useState(1);
  const [reason, setReason] = useState('');
  const [reasonDetails, setReasonDetails] = useState('');
  const [cancelling, setCancelling] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  // Reset state when modal opens
  React.useEffect(() => {
    if (isOpen) {
      setStep(1);
      setReason('');
      setReasonDetails('');
      setError(null);
      setResult(null);
    }
  }, [isOpen]);

  const handleCancel = async () => {
    if (!reason || !reasonDetails.trim()) {return;}

    try {
      setCancelling(true);
      setError(null);

      const response = await apiRequest(`/api/v1/guides/bookings/${bookingId}/cancel`, {
        method: 'POST',
        body: JSON.stringify({
          reason,
          reasonDetails: reasonDetails.trim(),
        }),
      });

      if (response.success) {
        setResult(response.data);
        setStep(2);
        if (onCancelled) {
          onCancelled(response.data);
        }
      }
    } catch (err) {
      setError(err.message || 'Failed to cancel booking');
    } finally {
      setCancelling(false);
    }
  };

  const handleClose = () => {
    onClose();
  };

  // Step 1: Warning & Reason
  const renderForm = () => (
    <div className="space-y-4">
      {/* Warning Banner */}
      <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
        <div className="flex items-start space-x-3">
          <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-amber-800">
              Full refund to customer
            </p>
            <p className="text-xs text-amber-700 mt-1">
              Cancelling this booking will result in a full refund to the customer.
              This action cannot be undone.
            </p>
          </div>
        </div>
      </div>

      {/* Reason Selector */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Reason for Cancellation <span className="text-red-500">*</span>
        </label>
        <select
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-brand-primary"
        >
          <option value="">Select a reason...</option>
          {GUIDE_REASONS.map((r) => (
            <option key={r.value} value={r.value}>
              {r.label}
            </option>
          ))}
        </select>
      </div>

      {/* Details Textarea */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Explanation <span className="text-red-500">*</span>
        </label>
        <textarea
          value={reasonDetails}
          onChange={(e) => setReasonDetails(e.target.value)}
          placeholder="Please explain the reason for cancellation"
          rows={4}
          className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm resize-none focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-brand-primary"
        />
      </div>

      {/* Error */}
      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-center space-x-2">
          <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0" />
          <span className="text-sm text-red-700">{error}</span>
        </div>
      )}
    </div>
  );

  // Step 2: Result
  const renderResult = () => {
    if (error && !result) {
      return (
        <div className="text-center py-6">
          <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Cancellation Failed</h3>
          <p className="text-red-600 mb-4">{error}</p>
          <button
            onClick={() => {
              setStep(1);
              setError(null);
            }}
            className="text-sm text-brand-primary hover:text-brand-dark"
          >
            Try Again
          </button>
        </div>
      );
    }

    return (
      <div className="text-center py-6">
        <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Booking Cancelled</h3>
        <p className="text-gray-600">
          The customer will receive a full refund. They&apos;ll be notified by email.
        </p>
      </div>
    );
  };

  // Footer
  const renderFooter = () => {
    if (step === 1) {
      return (
        <>
          <button
            onClick={handleClose}
            className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition"
          >
            Go Back
          </button>
          <button
            onClick={handleCancel}
            disabled={!reason || !reasonDetails.trim() || cancelling}
            className="flex items-center space-x-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition"
          >
            <XCircle className="w-4 h-4" />
            <span>{cancelling ? 'Cancelling...' : 'Cancel Booking \u2014 Full Refund to Customer'}</span>
          </button>
        </>
      );
    }

    return (
      <button
        onClick={handleClose}
        className="px-6 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition"
      >
        Close
      </button>
    );
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={step === 1 ? onClose : handleClose}
      title={step === 1 ? 'Cancel Booking' : 'Cancellation Complete'}
      footer={renderFooter()}
      closeOnEscape={step === 1}
      closeOnBackdrop={false}
    >
      {step === 1 && renderForm()}
      {step === 2 && renderResult()}
    </Modal>
  );
}

BookingCancellationGuide.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  bookingId: PropTypes.string.isRequired,
  onCancelled: PropTypes.func,
};
